import { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useDepartmentDataWeeks } from "../../hooks/useDepartmentDataWeeks";
import { HOSPITAL_DEMO } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { weekPropertyColor } from "../../utils/weekPropertyColor";
import { ChartCensus } from "../chartCensus";
import { ChartDateArea } from "../chartDateArea";
import { ChartOverviewModule } from "../chartOverviewModule";
import { ChartScoreArea } from "../chartScoreArea";
import { ChartStatus } from "../chartStatus";
import { DateSlider } from "../dateSlider";
import { PageScheduleFallback } from "../StaffingChanges/PageScheduleFallback";
import Style from "./PlanningOverview.module.scss";

export const PlanningOverview: FC = () => {
	const { t } = useTranslation();
	const { getColorByDifficultyName, isHospitalView } = useHospitalData();
	const planning = useDepartmentDataWeeks();
	const { weeks } = planning;
	const [weekLoad, setWeekLoad] = useState(t("Medium"));
	const [weekSeason, setWeekSession] = useState(t("Medium"));
	const [workForce, setWorkForce] = useState(t("Medium"));
	const [weekLoadText, setWeekLoadText] = useState("");
	const [weekSeasonText, setWeekSeasonText] = useState("");
	const [weekWordkForceText, setWorkForceText] = useState("");
	const [activeBoxIndex, setActiveBoxIndex] = useState(-1);
	const [weekLoadColor, setWeekLoadColor] = useState("");
	const [weekSeasonColor, setWeekSessionColor] = useState("");
	const [workForceColor, setWorkForceColor] = useState("");
	const [hoveredStartDate, setHoveredStartDate] = useState<string>();

	const handleMouseEnter = (
		weekIndex: number,
		weekLoad: string,
		weekSeason: string,
		workForce: string,
		loadText: string,
		seasonText: string,
		wordkForceText: string,
		startDate: string,
	) => {
		setWeekLoad(weekLoad);
		setWeekSession(weekSeason);
		setWorkForce(workForce);
		setWeekLoadText(loadText);
		setWeekSeasonText(seasonText);
		setWorkForceText(wordkForceText);
		setWeekLoadColor(getColorByDifficultyName(weekLoad));
		setWeekSessionColor(getColorByDifficultyName(weekSeason));
		setWorkForceColor(getColorByDifficultyName(workForce));
		setHoveredStartDate(startDate);
		trackEvent("Planning - Week Hovered", { weekIndex });
	};

	useEffect(() => {
		const [firstWeek] = weeks;
		if (!firstWeek) return;
		setWeekLoad(firstWeek.weekLoad);
		setWeekSession(firstWeek.weekSeason);
		setWorkForce(firstWeek.workForce);
		setWeekLoadText(firstWeek.loadText);
		setWeekSeasonText(firstWeek.seasonText);
		setWorkForceText(firstWeek.workForceText);
		setWeekLoadColor(weekPropertyColor(firstWeek, "weekLoad"));
		setWeekSessionColor(weekPropertyColor(firstWeek, "weekSeason"));
		setWorkForceColor(weekPropertyColor(firstWeek, "workForce"));
	}, [weeks]);

	const groupOfWeeks = () => {
		const modulesPerGroup = 3;
		const groupedModules = [];
		for (let i = 0; i < weeks.length; i += modulesPerGroup) {
			const weekGroup = weeks.slice(i, i + modulesPerGroup);
			const weeklyModule = weekGroup.map((module: any, index: any) => (
				<div
					key={index}
					onMouseEnter={() =>
						handleMouseEnter(
							index,
							module.weekLoad,
							module.weekSeason,
							module.workForce,
							module.loadText,
							module.seasonText,
							module.workForceText,
							module.weeklyShifts[0]?.days[0]?.date,
						)
					}
					onClick={() => {
						setActiveBoxIndex(-1);
						setActiveBoxIndex(module.weeklyShifts[0]?.days[0]?.date);
						handleMouseEnter(
							index,
							module.weekLoad,
							module.weekSeason,
							module.workForce,
							module.loadText,
							module.seasonText,
							module.workForceText,
							module.weeklyShifts[0]?.days[0]?.date,
						);
					}}
				>
					<ChartOverviewModule
						key={index}
						color={getColorByDifficultyName(module.weekDifficulty)}
						status={t(module.weekScore)}
						percentage={module.confidence}
						startDate={module.weeklyShifts[0]?.days[0]?.date}
						endDate={module.weeklyShifts[0]?.days[6]?.date}
						isActive={module.weeklyShifts[0]?.days[0]?.date === activeBoxIndex}
					/>
				</div>
			));

			groupedModules.push(
				<div className={Style.modules} key={i}>
					{weeklyModule}
				</div>,
			);
		}
		return <div>{groupedModules}</div>;
	};
	const hoveredDate = useMemo(() => (hoveredStartDate ? DateTime.fromISO(hoveredStartDate) : DateTime.invalid("no date")), [hoveredStartDate]);

	if (planning.isLoading || planning.isError) return <PageScheduleFallback load={planning} dataTestId="planning" />;

	return (
		<>
			<div className={Style.statusContent}>
				<div className={Style.content}>
					<ChartDateArea day={hoveredDate} color={weekLoadColor} labelEnabled label={t(weekLoadText)} />
					<div style={{ marginTop: "10px", marginBottom: "10px" }}>
						<ChartCensus
							dailyCensusTitle={t("Season")}
							dailyCensusValue={t(weekSeason)}
							dailyCensusColor={weekSeasonColor}
							labelEnabled
							label={t(weekSeasonText)}
						/>
					</div>
					<ChartScoreArea
						patientScoreText={t("Work Force")}
						patientScore={t(workForce)}
						scoreColor={workForceColor}
						labelEnabled
						label={t(weekWordkForceText)}
					/>
				</div>
				<ChartStatus
					status={t("Status")}
					low={t("Low")}
					average={t("Medium")}
					high={t("High")}
					lowColor={isHospitalView ? HOSPITAL_DEMO.NODE_LOW : undefined}
					averageColor={isHospitalView ? HOSPITAL_DEMO.NODE_MEDIUM : undefined}
					highColor={isHospitalView ? HOSPITAL_DEMO.NODE_HIGH : undefined}
				/>
			</div>
			<div className={Style.chartContainer}>
				<DateSlider isFullDates fullDatesWeeks={weeks} />
				<div style={{ overflowY: "auto", height: "100%" }}>{groupOfWeeks()}</div>
			</div>
		</>
	);
};
